import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Badge, BilingualField, Button, TextField } from "@core/components";
import { useIdempotencyKey } from "@core/hooks";
import { cn } from "@core/utils";
import { useAppel } from "../../detail";
import {
  useSaveProgramme,
  valuesOfAppel,
  validateProgramme,
  type ProgrammeValues,
} from "../../programme";
import type { Appel } from "../../_shared/types";
import { algiersParts, formatCalendarDate } from "../format";

function Card({
  title,
  badge,
  children,
}: {
  title: string;
  badge: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-md rounded-lg border border-(--border-default) bg-surface-card p-lg">
      <header className="flex items-center justify-between gap-sm">
        <h2 className="text-md-semibold text-(--text-primary)">{title}</h2>
        <Badge outlined>{badge}</Badge>
      </header>
      {children}
    </section>
  );
}

function FactLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-xxs sm:flex-row sm:gap-lg">
      <dt className="w-28 shrink-0 text-sm-regular text-(--text-secondary)">
        {label}
      </dt>
      <dd className="text-sm-medium text-(--text-primary)">{value || "—"}</dd>
    </div>
  );
}

function ProgrammeForm({ appel }: { appel: Appel }) {
  const { t } = useTranslation("appels");
  const navigate = useNavigate();
  const save = useSaveProgramme(appel);
  const [values, setValues] = useState<ProgrammeValues>(() =>
    valuesOfAppel(appel)
  );
  const [submitted, setSubmitted] = useState(false);
  const { key, rotate } = useIdempotencyKey(JSON.stringify(values));

  const errors = submitted ? validateProgramme(values) : {};
  const canSave = appel.available_actions.save_programme === true;
  const message = (code: string | undefined): string | undefined =>
    code ? t("errors.required") : undefined;

  const cohort = appel.cohort;
  const opening = algiersParts(appel.opening_at);
  const closing = algiersParts(appel.closing_at);
  const margin = cohort.interview_margin_percent;
  const delay = cohort.response_delay_days;

  const next = () => {
    setSubmitted(true);
    if (Object.keys(validateProgramme(values)).length > 0) return;
    save.mutate(
      { values, idempotencyKey: key },
      {
        onSuccess: (saved) => {
          void navigate({
            to: "/cohorts/$cohortId/appels/$appelId/eligibility",
            params: { cohortId: saved.cohort_id, appelId: saved.id },
          });
        },
        onError: () => {
          rotate();
        },
      }
    );
  };

  const textField = (name: keyof ProgrammeValues, multiline = false) => (
    <BilingualField
      label={t(`programme.${name}`)}
      layout="columns"
      multiline={multiline}
      value={values[name]}
      onChange={(text) => {
        setValues({ ...values, [name]: text });
      }}
      isDisabled={!canSave || save.isPending}
      errorMessage={{
        fr: message(errors[`${name}.fr`]),
        en: message(errors[`${name}.en`]),
      }}
    />
  );

  return (
    <form
      noValidate
      className="flex flex-col gap-lg"
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        next();
      }}
    >
      {/* The cohort's numbers: read-only here, changed in the cohort's settings. Only step 1 shows
          them; once the step is passed they are not repeated. */}
      <Card title={t("programme.team.title")} badge={t("programme.team.badge")}>
        <div className="grid grid-cols-1 gap-md sm:grid-cols-2 lg:grid-cols-4">
          <TextField
            label={t("programme.team.places")}
            value={String(cohort.places)}
            isReadOnly
          />
          <TextField
            label={t("programme.team.waitlist")}
            description={t("programme.team.waitlist-hint")}
            value={String(cohort.waitlist_places)}
            isReadOnly
          />
          <TextField
            label={t("programme.team.margin")}
            description={
              appel.interviews_count === null
                ? undefined
                : t("programme.team.interviews", {
                    count: appel.interviews_count,
                  })
            }
            value={margin === null ? "" : String(margin)}
            placeholder="—"
            isReadOnly
          />
          <TextField
            label={t("programme.team.delay")}
            description={t("programme.team.delay-hint")}
            value={delay === null ? "" : String(delay)}
            placeholder="—"
            isReadOnly
          />
        </div>
        <Link
          to="/cohorts/$cohortId/settings"
          params={{ cohortId: appel.cohort_id }}
          className="self-start rounded-sm text-sm-semibold text-(--text-link) underline underline-offset-2 outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
        >
          {t("programme.team.edit")}
        </Link>
      </Card>

      <Card
        title={t("programme.candidates.title")}
        badge={t("programme.candidates.badge")}
      >
        {textField("presentation", true)}
        {textField("rhythm")}
        {textField("location")}

        <dl className="flex flex-col gap-sm pt-xs">
          <FactLine
            label={t("programme.facts.appel")}
            value={`${opening.date} ${t("programme.facts.at")} ${opening.time} → ${closing.date} ${t("programme.facts.at")} ${closing.time}`}
          />
          <FactLine
            label={t("programme.facts.programme")}
            value={
              cohort.program_start_date && cohort.program_end_date
                ? `${formatCalendarDate(cohort.program_start_date)} → ${formatCalendarDate(cohort.program_end_date)}`
                : ""
            }
          />
          <FactLine
            label={t("programme.facts.answers")}
            value={formatCalendarDate(appel.answers_date)}
          />
        </dl>
      </Card>

      {canSave ? (
        <div className={cn("flex justify-end")}>
          <Button
            type="submit"
            isPending={save.isPending}
            isDisabled={save.isPending}
          >
            {t("programme.next")}
          </Button>
        </div>
      ) : null}
    </form>
  );
}

/**
 * Step 1, « Programme et places » (Figma "Appel config, step 1" and its error and filled frames):
 * « L'équipe », the cohort's numbers read-only (with a link to change them in the cohort's
 * settings), then « Les candidats », the six texts the applicants will read in French and English,
 * and three read-only lines (the appel's dates, the programme's dates, the answers date).
 *
 * « Suivant » checks the six texts and shows every missing one at once, each under its own field;
 * when all are there it saves the step, ticks it, and opens step 2.
 */
export function ProgrammeScreen({ appelId }: { appelId: string }) {
  const appel = useAppel(appelId);
  if (!appel.data) return null;
  // Keyed on the revision: after a save the form starts again from what the server holds.
  return <ProgrammeForm key={appel.data.revision} appel={appel.data} />;
}
